import type { ProjectQuote } from './projectCatalog';
import { PROJECT_PRICING, supportMonthlyPrice } from './projectPricing.ts';
import { formatPrice } from './shop.ts';
export type ObjectKind = 'shop' | 'home' | 'warehouse';
export type CameraKind = 'indoor' | 'outdoor';
export interface CablePoint {
  x: number;
  y: number;
}
export const MAX_CABLE_POINTS = 24;
export const DEFAULT_CAMERA_BITRATE = 2;
export const DEFAULT_ARCHIVE = { days: 7, hours: 14 } as const;
export interface PlanCamera {
  id: string;
  name: string;
  kind: CameraKind;
  x: number;
  y: number;
  angle: number;
  fov: number;
  productSlug?: string;
  cablePoints?: CablePoint[];
}
export const EQUIPMENT_SLOTS = [
  'server',
  'switch',
  'storage',
  'enclosure',
  'router',
  'ups',
  'cable-indoor',
  'cable-outdoor',
  'extender',
] as const;
export type EquipmentSlot = (typeof EQUIPMENT_SLOTS)[number];
export interface ProjectDraft {
  version: 1;
  name: string;
  object: ObjectKind;
  width: number;
  height: number;
  cameras: PlanCamera[];
  server: { x: number; y: number };
  image: string;
  imageName: string;
  days: number;
  bitrate: number;
  hours: number;
  archiveDefaultsVersion: 1;
  includeArchive: boolean;
  installation: boolean;
  includeRouter: boolean;
  includeUps: boolean;
  includeService: boolean;
  needsTranscoding: boolean;
  equipmentOverrides: Partial<Record<EquipmentSlot, string>>;
}
export const objectNames = { shop: 'Магазин / офис', home: 'Дом / участок', warehouse: 'Склад' };
export const cameraNames = { indoor: 'В помещении', outdoor: 'На улице' };
export const PROJECT_STORAGE_KEY = 'videosurv-project-v1';
export const MAX_CAMERAS = 32;
export const OUTDOOR_MARGIN = 0.2;
export function cameraPosition(kind: CameraKind, x: number, y: number) {
  const min = kind === 'outdoor' ? -OUTDOOR_MARGIN : 0.02;
  const max = kind === 'outdoor' ? 1 + OUTDOOR_MARGIN : 0.98;
  return { x: clamp(x, min, max), y: clamp(y, min, max) };
}
export function createProject(): ProjectDraft {
  return {
    version: 1,
    name: 'Мой проект',
    object: 'shop',
    width: 20,
    height: 14,
    cameras: [
      { id: 'cam-1', name: 'Вход', kind: 'outdoor', x: 0.14, y: 1.1, angle: 90, fov: 85 },
      { id: 'cam-2', name: 'Торговый зал', kind: 'indoor', x: 0.16, y: 0.18, angle: 40, fov: 90 },
      { id: 'cam-3', name: 'Касса', kind: 'indoor', x: 0.84, y: 0.18, angle: 140, fov: 75 },
      { id: 'cam-4', name: 'Склад', kind: 'indoor', x: 0.84, y: 0.78, angle: 225, fov: 85 },
    ],
    server: { x: 0.84, y: 0.48 },
    image: '',
    imageName: '',
    days: DEFAULT_ARCHIVE.days,
    bitrate: DEFAULT_CAMERA_BITRATE,
    hours: DEFAULT_ARCHIVE.hours,
    archiveDefaultsVersion: 1,
    includeArchive: true,
    installation: true,
    includeRouter: false,
    includeUps: false,
    includeService: false,
    needsTranscoding: false,
    equipmentOverrides: {},
  };
}
export function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}
export function cameraLabel(count: number) {
  const form = new Intl.PluralRules('ru-RU').select(count);
  return `${count} ${form === 'one' ? 'камера' : form === 'few' ? 'камеры' : 'камер'}`;
}
export function cableRoute(camera: PlanCamera, server: CablePoint): CablePoint[] {
  return [camera, ...(camera.cablePoints ?? [{ x: server.x, y: camera.y }]), server];
}
export function cableLength(
  camera: PlanCamera,
  draft: Pick<ProjectDraft, 'server' | 'width' | 'height'>,
) {
  const route = cableRoute(camera, draft.server);
  return route
    .slice(1)
    .reduce(
      (sum, p, i) =>
        sum + Math.hypot((p.x - route[i].x) * draft.width, (p.y - route[i].y) * draft.height),
      0,
    );
}
export function projectMetrics(draft: ProjectDraft) {
  const count = draft.cameras.length;
  // Ignore binary rounding noise at an exact whole-metre boundary.
  const metres = (n: number) => Math.max(0, Math.ceil(n - Number.EPSILON * Math.max(1, n) * 4));
  // Measure the same polyline that is displayed on the plan, then apply reserve once.
  const lengths = draft.cameras.map((c) => cableLength(c, draft));
  const cableRouteByKind = { indoor: 0, outdoor: 0 };
  draft.cameras.forEach((camera, index) => {
    cableRouteByKind[camera.kind] += lengths[index];
  });
  const cableByKind = {
    indoor: metres(cableRouteByKind.indoor * (1 + PROJECT_PRICING.cableReserve)),
    outdoor: metres(cableRouteByKind.outdoor * (1 + PROJECT_PRICING.cableReserve)),
  };
  cableRouteByKind.indoor = metres(cableRouteByKind.indoor);
  cableRouteByKind.outdoor = metres(cableRouteByKind.outdoor);
  const cable = cableByKind.indoor + cableByKind.outdoor;
  // Mbit/s -> MB/s -> decimal TB, with 20% storage reserve.
  const storageTB =
    draft.includeArchive === false
      ? 0
      : ((((count * draft.bitrate) / 8) * 3600 * draft.hours * draft.days) / 1_000_000) *
        (1 + PROJECT_PRICING.storageReserve);
  return {
    count,
    cable,
    cableByKind,
    cableRouteByKind,
    cableRoute: cableRouteByKind.indoor + cableRouteByKind.outdoor,
    storageTB,
    longRuns: lengths.filter((n) => n > 90).length,
  };
}
export function projectText(draft: ProjectDraft, quote?: ProjectQuote) {
  const m = projectMetrics(draft);
  const money = formatPrice;
  return [
    `Robotics Vision — ${draft.name}`,
    '',
    `Объект: ${objectNames[draft.object]}`,
    `Габариты схемы: ${draft.width} × ${draft.height} м (${draft.width * draft.height} м²)`,
    `Камеры: ${m.count}`,
    ...draft.cameras.map(
      (c, i) =>
        `  ${i + 1}. ${c.name} — ${cameraNames[c.kind]}, направление ${c.angle}°, обзор ${c.fov}°`,
    ),
    ...(draft.includeArchive === false
      ? ['Без архива: только просмотр в реальном времени, записи не сохраняются.']
      : [
          `Архив: ${draft.days} дней, ${draft.hours} ч/сутки, ${draft.bitrate} Мбит/с на камеру`,
          `Требуемая ёмкость с запасом 20%: ${m.storageTB.toFixed(2)} ТБ`,
        ]),
    `Роутер: ${draft.includeRouter ? 'Новый, включён в смету' : 'Используем существующий, покупка не включена'}`,
    ...(quote
      ? [
          `PoE-порты: ${quote.poePorts}`,
          `Выбранные диски: ${draft.includeArchive === false ? 'Не нужны — без записи' : quote.storageTB ? `${quote.storageTB} ТБ` : m.count ? 'Нужен подбор' : 'Не нужны'}`,
        ]
      : []),
    `Кабель: ${m.cable} м`,
    `Монтаж: ${draft.installation ? 'Нужен' : 'Не нужен'}`,
    `Сопровождение: ${draft.includeService ? (supportMonthlyPrice(draft.cameras.length) === null ? 'По отдельному расчёту' : money(supportMonthlyPrice(draft.cameras.length)!) + ' / мес., отдельно от стоимости проекта') : 'Не выбрано'}`,
    `План: ${draft.imageName || 'Условная схема'}`,
    '',
    'ОБОРУДОВАНИЕ ПО ЦЕНАМ КАТАЛОГА',
    ...(quote
      ? quote.lines.map(
          (line) =>
            `${line.product.title} (${line.product.model}) — ${line.quantity} ${line.unit} × ${money(line.unitPrice)} = ${money(line.total)}${line.detail ? `; ${line.detail}` : ''}`,
        )
      : ['Цены каталога ещё не загружены. Обновите расчёт перед заказом.']),
    ...(quote?.notes || []),
    ...(quote?.missing.length
      ? [
          `Требуют подбора: ${quote.missing.join('; ')}`,
          `Учтённые позиции: ${money(quote.subtotal)}`,
          'Полный итог недоступен до подбора всех позиций.',
        ]
      : quote
        ? [`Итого оборудование: ${money(quote.equipmentTotal!)}`]
        : []),
    ...(quote
      ? [
          '',
          'МОНТАЖ И НАСТРОЙКА',
          ...(draft.installation
            ? quote.installationLines.map(
                (line) =>
                  `${line.title} — ${line.quantity} ${line.unit} × ${money(line.unitPrice)} = ${money(line.total)}`,
              )
            : ['Монтаж не выбран']),
          `Итого работы: ${money(quote.installationTotal)}`,
          ...(quote.total !== null ? [`ИТОГО ПРОЕКТ: ${money(quote.total)}`] : []),
        ]
      : [`Монтаж и настройка: ${draft.installation ? 'Цены ещё не загружены' : 'Не выбраны'}`]),
    '',
    'Цены оборудования берутся из опубликованного каталога на момент расчёта. Стоимость работ предварительная для обычного монтажа без штробления и высотных работ. Питание, подключение дисков, топологию и дополнительные расходные материалы уточняем по объекту. Угол обзора не учитывает стены и препятствия.',
  ].join('\n');
}
export function parseProject(input: unknown): ProjectDraft {
  if (!input || typeof input !== 'object') throw new Error('В файле нет проекта Robotics Vision.');
  const p = input as Record<string, unknown>;
  const fail = () => {
    throw new Error('Формат проекта не поддерживается или данные повреждены.');
  };
  const num = (n: unknown, min: number, max: number) => {
    if (typeof n !== 'number' || !Number.isFinite(n) || n < min || n > max) fail();
    return n as number;
  };
  const str = (s: unknown, max: number) => {
    if (typeof s !== 'string' || s.length > max) fail();
    return s as string;
  };
  if (
    p.version !== 1 ||
    !['shop', 'home', 'warehouse'].includes(String(p.object)) ||
    !Array.isArray(p.cameras) ||
    p.cameras.length > MAX_CAMERAS ||
    typeof p.installation !== 'boolean'
  )
    fail();
  const ids = new Set<string>();
  const cameras = (p.cameras as unknown[]).map((item) => {
    if (!item || typeof item !== 'object') fail();
    const c = item as Record<string, unknown>;
    const id = str(c.id, 80);
    if (
      !id ||
      ['server', 'pan'].includes(id) ||
      ids.has(id) ||
      !['indoor', 'outdoor'].includes(String(c.kind))
    )
      fail();
    ids.add(id);
    let cablePoints: CablePoint[] | undefined;
    if (c.cablePoints !== undefined) {
      if (!Array.isArray(c.cablePoints) || c.cablePoints.length > MAX_CABLE_POINTS) fail();
      cablePoints = (c.cablePoints as unknown[]).map((value) => {
        if (!value || typeof value !== 'object') fail();
        const point = value as Record<string, unknown>;
        return {
          x: num(point.x, -OUTDOOR_MARGIN, 1 + OUTDOOR_MARGIN),
          y: num(point.y, -OUTDOOR_MARGIN, 1 + OUTDOOR_MARGIN),
        };
      });
    }
    return {
      id,
      name: str(c.name, 48).trim() || 'Камера',
      kind: c.kind as CameraKind,
      x: num(
        c.x,
        c.kind === 'outdoor' ? -OUTDOOR_MARGIN : 0,
        c.kind === 'outdoor' ? 1 + OUTDOOR_MARGIN : 1,
      ),
      y: num(
        c.y,
        c.kind === 'outdoor' ? -OUTDOOR_MARGIN : 0,
        c.kind === 'outdoor' ? 1 + OUTDOOR_MARGIN : 1,
      ),
      angle: num(c.angle, 0, 360),
      fov: num(c.fov, 30, 120),
      ...(cablePoints === undefined ? {} : { cablePoints }),
      ...(c.productSlug === undefined ? {} : { productSlug: str(c.productSlug, 200) }),
    };
  });
  const server = p.server as Record<string, unknown>;
  if (!server || typeof server !== 'object') fail();
  const image = str(p.image, 5_000_000);
  if (image && !/^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/.test(image)) fail();
  if (
    (p.includeArchive !== undefined && typeof p.includeArchive !== 'boolean') ||
    (p.includeRouter !== undefined && typeof p.includeRouter !== 'boolean') ||
    (p.includeUps !== undefined && typeof p.includeUps !== 'boolean') ||
    (p.includeService !== undefined && typeof p.includeService !== 'boolean')
  )
    fail();
  const equipmentOverrides: ProjectDraft['equipmentOverrides'] = {};
  if (p.equipmentOverrides !== undefined) {
    if (
      !p.equipmentOverrides ||
      typeof p.equipmentOverrides !== 'object' ||
      Array.isArray(p.equipmentOverrides)
    )
      fail();
    for (const [key, value] of Object.entries(p.equipmentOverrides as Record<string, unknown>)) {
      if (!EQUIPMENT_SLOTS.includes(key as EquipmentSlot)) fail();
      const slug = str(value, 200);
      if (!/^[a-z0-9-]+$/.test(slug)) fail();
      equipmentOverrides[key as EquipmentSlot] = slug;
    }
  }
  if (p.needsTranscoding !== undefined && typeof p.needsTranscoding !== 'boolean') fail();
  // Older drafts used a selectable bitrate. The planner now uses the fixed stream preset.
  num(p.bitrate, 1, 12);
  const days = num(p.days, 1, 90),
    hours = num(p.hours, 1, 24);
  if (p.archiveDefaultsVersion !== undefined && p.archiveDefaultsVersion !== 1) fail();
  // Migrate only the former default. Once saved, explicit archive choices stay intact.
  const oldArchiveDefault = p.archiveDefaultsVersion === undefined && days === 14 && hours === 24;
  return {
    version: 1,
    name: str(p.name, 80).trim() || 'Мой проект',
    object: p.object as ObjectKind,
    width: num(p.width, 5, 200),
    height: num(p.height, 5, 200),
    cameras,
    server: { x: num(server.x, 0, 1), y: num(server.y, 0, 1) },
    image,
    imageName: str(p.imageName, 160),
    days: oldArchiveDefault ? DEFAULT_ARCHIVE.days : days,
    bitrate: DEFAULT_CAMERA_BITRATE,
    hours: oldArchiveDefault ? DEFAULT_ARCHIVE.hours : hours,
    archiveDefaultsVersion: 1,
    includeArchive: p.includeArchive === undefined ? true : (p.includeArchive as boolean),
    installation: p.installation as boolean,
    includeRouter: p.includeRouter === undefined ? true : (p.includeRouter as boolean),
    includeUps: p.includeUps === undefined ? false : (p.includeUps as boolean),
    equipmentOverrides,
    needsTranscoding: p.needsTranscoding === true,
    includeService: p.includeService === undefined ? false : (p.includeService as boolean),
  };
}
